<template>
  <!-- prettier-ignore -->
  <the-typography
    class="footer"
    type="bodyLargeShort"
  >
    <template v-if="$props.metaOptions.aide">
      <div class="footer-sections">
        <ul class="footer-list">
          <li>
            <a
              target="_blank"
              :href="$t('application.footer.imprint.link')"
              v-text="$t('application.footer.imprint.title')"
            />
          </li>
          <li>
            <a
              target="_blank"
              :href="$t('application.footer.legalNotice.link')"
              v-text="$t('application.footer.legalNotice.title')"
            />
          </li>
        </ul>
      </div>
    </template>
    <template v-else>
      <div class="footer-sections">
        <div class="footer-section">
          <template v-if="contactInfo?.phone?.enabled">
            <strong v-text="contactInfo.phone.name" />
            <div
              v-for="(time, index) in contactInfo.phone.times"
              :key="index"
              v-text="`${time.days} ${time.hours}`"
            />
            <a
              :class="{'phone--disabled': !contactInfo.phone.active}"
              :href="`tel:${contactInfo.phone.nr}`"
            >
              <the-icon
                art="solid"
                name="phone"
              />
              {{ contactInfo.phone.nr }}
            </a>

            <template v-if="basketStore.basket.channel === CHANNEL.PARTNERDVP">
              <a
                :href="`mailto:${contactInfo.email[basketStore.basket.language.toLowerCase()]}`"
              >
                <the-icon
                  art="solid"
                  name="at"
                />
                {{ contactInfo.email[basketStore.basket.language.toLowerCase()] }}
              </a>
            </template>
          </template>
        </div>

        <div class="footer-section">
          <strong v-text="$t('application.footer.help')" />

          <!-- id used for teleport target -->
          <div
            v-if="showOverview"
            id="ovpContractOverview"
          >
            <template v-if="showDefaultOverviewLink">
              <Form.Button
                v-unblu-hidden
                text-link
                :text="$t('application.footer.showOverviewPdf')"
                @click="openDocument"
              />
            </template>
          </div>
          <a
            target="_blank"
            :href="$t('screen.submit.furtherOptions.cancellation.href')"
            v-text="$t('screen.submit.furtherOptions.cancellation.link')"
          />
        </div>
      </div>

      <div class="footer-sections">
        <ul class="footer-list">
          <li>
            <a
              target="_blank"
              :href="$t('application.footer.imprint.link')"
              v-text="$t('application.footer.imprint.title')"
            />
          </li>
          <li>
            <a
              target="_blank"
              :href="$t('application.footer.legalNotice.link')"
              v-text="$t('application.footer.legalNotice.title')"
            />
          </li>
          <li
            class="version"
            v-text="`v${version$}`"
          />
        </ul>
      </div>
    </template>
  </the-typography>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useRoute } from 'vue-router'

import basketAPI from '@/api/basket'
import basketStore from '@/store/basket'
import useContact from '@/hooks/useContact'
import useStatus from '@/hooks/useStatus'

import * as Form from '@/components/Form'

import { CHANNEL, ROUTE_NAME } from '@/config/constants'

// HOOKS
const { contactInfo } = useContact()
const route = useRoute()
const { isOnboardingSecured } = useStatus()

// INIT
const version$ = inject('version$')
defineProps({
  metaOptions: {
    type: Object,
    default: () => {},
  },
})

// COMPUTED
const showDefaultOverviewLink = computed(() => {
  return !route.meta.hideDefaultOverviewLink
})

const showOverview = computed(() => {
  return isOnboardingSecured.value && route.name !== ROUTE_NAME.REENTRY
})

// METHODS
function openDocument() {
  basketAPI.getDocument({
    basketId: basketStore.basket.basketId,
    fileName: 'overview',
  })
}
</script>

<style name="global">
#reentryModal {
  padding: 15px 15px 0;
}

#reentryModal {
  form {
    display: flex;
    flex-direction: column;
    margin-top: 20px;

    button {
      margin-top: 20px;
      min-width: 200px;
      align-self: flex-end;
    }
  }
}
</style>

<style name="mobile" scoped>
.footer {
  display: flex;
  flex-direction: column;
  padding: 50px 15px;
  color: var(--c-primary-neutral-1);
}

.footer-sections {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--c-secondary-neutral-3);
}

.footer-section {
  flex: 1 1 auto;
  margin-bottom: 25px;

  a {
    display: block;
  }
}

.footer-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  list-style: none;
  width: 100%;
  padding: 0;

  li {
    padding-bottom: unset;
    list-style: none;

    a {
      font-size: 12px;
      font-weight: 600;
      text-decoration: none;
    }
  }
}

.phone--disabled {
  pointer-events: none;
  color: var(--c-secondary-neutral-2);

  & :deep(.icon) {
    color: var(--c-secondary-neutral-2);
  }
}

.version {
  color: var(--c-black);
  font-size: 12px;
  font-weight: 100;
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .footer {
    padding: 50px 15px;
  }

  .footer-sections {
    display: flex;
    flex-direction: row;
  }

  .footer-section {
    flex: 0 1 auto;
    min-width: 300px;
    min-height: 100px;
    margin-bottom: 0;
  }
}
</style>
